import type { ITableData } from "@focus/focusUI/lib/components/Tables/FocusTable/Entities";
import type { CellContext } from "@tanstack/table-core";
import type { ReactNode } from "react";
import type { Contact, Organization } from "../../types/phonebook";

type PhonebookRow = (Contact | Organization) & {
  organization?: string;
  isUnassigned?: boolean;
  subRows?: Contact[];
};

type PhonebookCellContext = CellContext<ITableData, unknown>;

const emptyValue = "-";

const isEmpty = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

const displayValue = (value: unknown) => {
  if (isEmpty(value)) return emptyValue;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const getOriginal = ({ row }: PhonebookCellContext) =>
  row.original as PhonebookRow;

const getContactInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";

  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
};

const MiniBadge = ({
  children,
  tone = "slate",
}: {
  children: ReactNode;
  tone?: "amber" | "emerald" | "slate" | "sky";
}) => {
  const toneClass = {
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    sky: "bg-sky-50 text-sky-700 ring-sky-200",
    slate: "bg-slate-100 text-slate-600 ring-slate-200",
  }[tone];

  return (
    <span
      className={`inline-flex w-fit shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ring-1 ring-inset ${toneClass}`}
    >
      {children}
    </span>
  );
};

export const PhonebookOrganizationCell = (cell: PhonebookCellContext) => {
  const row = getOriginal(cell);
  const value = cell.getValue();
  const name = displayValue(value || row.name || row.organization);

  if (cell.row.depth > 0) {
    const contactOrganizations = (row as Contact).organizations ?? [];

    if (!contactOrganizations.length) {
      return <span className="text-sm font-medium text-slate-400">-</span>;
    }

    return (
      <div className="flex min-w-[180px] flex-wrap gap-1.5 py-1">
        {contactOrganizations.map((organization) => (
          <MiniBadge key={organization} tone="sky">
            {organization}
          </MiniBadge>
        ))}
      </div>
    );
  }

  const contactCount = cell.row.subRows?.length ?? row.subRows?.length ?? 0;
  const address = displayValue((row as Organization).address);

  return (
    <div className="flex min-w-[240px] flex-col gap-1 py-2">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="truncate text-sm font-bold text-slate-950">
          {name}
        </span>
        <MiniBadge tone={row.isUnassigned ? "amber" : "slate"}>
          {row.isUnassigned ? "Other" : "Organization"}
        </MiniBadge>
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-medium text-slate-500">
        <span>{contactCount} contacts</span>
        <span className="text-slate-300">/</span>
        <span className="max-w-[260px] truncate">{address}</span>
      </div>
    </div>
  );
};

export const PhonebookContactCell = (cell: PhonebookCellContext) => {
  if (cell.row.depth === 0) {
    return (
      <span className="text-xs font-semibold uppercase text-slate-400">
        Organization group
      </span>
    );
  }

  const row = getOriginal(cell) as Contact;
  const name = displayValue(cell.getValue() || row.contactName);
  const role = displayValue(row.role);

  return (
    <div className="flex min-w-[220px] items-center gap-3 py-2">
      <div className="focusoc-theme-surface flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-bold">
        {getContactInitials(name)}
      </div>
      <div className="min-w-0">
        <div className="truncate text-sm font-bold text-slate-950">{name}</div>
        <div className="truncate text-xs font-medium text-slate-500">
          {role}
        </div>
      </div>
    </div>
  );
};

export const PhonebookBooleanCell = (cell: PhonebookCellContext) => {
  if (cell.row.depth > 0) {
    return <span className="text-sm font-medium text-slate-400">-</span>;
  }

  const value = cell.getValue();
  if (isEmpty(value)) {
    return <span className="text-sm font-medium text-slate-400">-</span>;
  }

  const enabled = Boolean(value);

  return (
    <MiniBadge tone={enabled ? "emerald" : "slate"}>
      {enabled ? "Yes" : "No"}
    </MiniBadge>
  );
};

export const PhonebookTextCell = (cell: PhonebookCellContext) => {
  const value = displayValue(cell.getValue());

  return (
    <span
      className={`block max-w-[260px] truncate text-sm font-medium ${
        value === emptyValue ? "text-slate-400" : "text-slate-700"
      }`}
      title={value}
    >
      {value}
    </span>
  );
};

export const PhonebookEmailCell = (cell: PhonebookCellContext) => {
  const value = displayValue(cell.getValue());

  if (value === emptyValue) {
    return <span className="text-sm font-medium text-slate-400">-</span>;
  }

  return (
    <a
      className="block max-w-[260px] truncate text-sm font-semibold text-sky-700 transition hover:text-sky-900 hover:no-underline"
      href={`mailto:${value}`}
      onClick={(event) => event.stopPropagation()}
      title={value}
    >
      {value}
    </a>
  );
};

export const PhonebookPhoneCell = (cell: PhonebookCellContext) => {
  const value = displayValue(cell.getValue());

  if (value === emptyValue) {
    return <span className="text-sm font-medium text-slate-400">-</span>;
  }

  return (
    <a
      className="block max-w-[180px] truncate text-sm font-semibold text-slate-700 transition hover:text-slate-950 hover:no-underline"
      href={`tel:${value.replace(/\s/g, "")}`}
      onClick={(event) => event.stopPropagation()}
      title={value}
    >
      {value}
    </a>
  );
};

export const PhonebookNotesCell = (cell: PhonebookCellContext) => {
  const value = displayValue(cell.getValue());

  return (
    <span
      className={`block max-w-[340px] truncate text-sm ${
        value === emptyValue ? "font-medium text-slate-400" : "text-slate-600"
      }`}
      title={value}
    >
      {value}
    </span>
  );
};
